import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import type { IntegrationLinkIssue } from '../../api/business/types';
import { formatDateTime } from '../../components/format';
import { useLanguage } from '../../i18n/LanguageContext';

/**
 * The connection token, shown this once (ADR-0007 §2): only its hash is kept, so closing the dialog loses it
 * and the way back is a new token.
 */
export const ConnectionTokenDialog = ({
  issue,
  onClose,
}: {
  issue: IntegrationLinkIssue;
  onClose: () => void;
}) => {
  const { language, t } = useLanguage();
  const [copied, setCopied] = useState(false);

  const copy = (): void => {
    void navigator.clipboard?.writeText(issue.connectionToken).then(() => setCopied(true));
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('connectionToken.title', { name: issue.link.name })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="warning" variant="outlined">
            {t('connectionToken.once')}
          </Alert>
          <TextField
            label={t('connectionToken.token')}
            value={issue.connectionToken}
            multiline
            InputProps={{
              readOnly: true,
              sx: { fontFamily: 'ui-monospace, monospace', fontSize: 13 },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton aria-label={t('connectionToken.copy')} onClick={copy} edge="end">
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            helperText={
              copied
                ? t('connectionToken.copied')
                : t('connectionToken.validUntil', {
                    when: formatDateTime(issue.link.connectionTokenExpiresAt, language),
                  })
            }
          />
          <Typography variant="subtitle2">{t('connectionToken.stepsTitle')}</Typography>
          <Typography component="ol" variant="body2" sx={{ pl: 2.5, m: 0, '& li': { mb: 0.75 } }}>
            <li>{t(`connectionToken.install.${issue.link.platform}`)}</li>
            <li>{t('connectionToken.step2')}</li>
            <li>{t('connectionToken.step3')}</li>
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" onClick={onClose}>
          {t('connectionToken.done')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
